import { CoreTypes, type Id, type LinkRecord, type ObjectRecord, type Primitive } from '@vp/shared-types';
import { boxBounds, rectsIntersect, worldToLocal, type CanvasDocument, type ChangeEvent, type Rect, type Vec2 } from '@vp/core';
import { buildPrimitives, frameTitleHeight, type DisplayContext, isTextDecoration } from './display';
import { SpatialIndex } from './spatial';
import { arrowSize, distToPolyline, pointInPolygon, shapeOutline } from './pathgeom';
import { computeRoute, pointsBox } from './interaction/connectors';
import { branchesOf, computeFolding, type Branch, type Folding } from './interaction/fold';

export interface RenderItem {
  id: Id;
  obj: ObjectRecord;
  rev: number;
  prims: Primitive[];
  /** World rect the item is clipped to (frames with clipping), or null. */
  clip?: Rect | null;
}

interface CacheEntry {
  rev: number;
  prims: Primitive[];
}

/**
 * Render-side view of a document: spatial index, layer ranks and a primitive cache.
 * Keeps logic (core) and display (render) separate (§11.3).
 */
export class Scene {
  readonly index = new SpatialIndex();
  private rank = new Map<Id, number>();
  private orderDirty = true;
  private maxZ: string | null = null;
  private cache = new Map<Id, CacheEntry>();
  private forced = new Map<Id, number>();
  private listeners = new Set<(ids: Id[] | null) => void>();
  private unsub: () => void;
  private forcedCounter = 0;
  /** Object whose text is currently edited in a DOM overlay. */
  hiddenText: Id | null = null;
  /** The label of an arrow as it is being typed (drawn in place of the stored one). */
  draftLabel: { id: Id; text: string } | null = null;
  private nextRank = 0;

  constructor(
    readonly doc: CanvasDocument,
    public displayCtx: DisplayContext,
  ) {
    this.rebuild();
    this.unsub = doc.onChange((e) => this.onDocChange(e));
  }

  dispose(): void {
    this.unsub();
    this.listeners.clear();
  }

  onInvalidate(fn: (ids: Id[] | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(ids: Id[] | null): void {
    for (const fn of this.listeners) fn(ids);
  }

  private folding: Folding = { hidden: new Set(), counts: new Map() };
  private branches = new Map<Id, Branch[]>();
  private branchByConn = new Map<Id, Branch>();
  private incoming = new Map<Id, Branch[]>();

  /** Hidden inside a collapsed branch: not drawn, not hit, not found by area queries. */
  isFolded(id: Id): boolean {
    return this.folding.hidden.has(id);
  }

  /** Shapes a collapsed branch (connector) folds away, all levels; 0 when not collapsed. */
  foldedCount(conn: Id): number {
    return this.folding.counts.get(conn) ?? 0;
  }

  /** Branches leaving a shape. */
  branchesFrom(id: Id): readonly Branch[] {
    return this.branches.get(id) ?? [];
  }

  /** Branches entering a shape. */
  branchesInto(id: Id): readonly Branch[] {
    return this.incoming.get(id) ?? [];
  }

  /** The branch a connector makes, or null when it does not join two shapes. */
  branchOf(conn: Id): Branch | null {
    return this.branchByConn.get(conn) ?? null;
  }

  /** Shapes folded away behind collapsed branches of `ids` (they travel with them). */
  foldedUnder(ids: Iterable<Id>): Id[] {
    const seen = new Set<Id>(ids);
    const out: Id[] = [];
    const queue: Id[] = [];
    const take = (id: Id) => {
      if (seen.has(id) || !this.isFolded(id)) return;
      seen.add(id);
      out.push(id);
      queue.push(id);
    };
    // from a visible shape only its own collapsed branches; inside the folded part, any connector either way
    for (const id of [...seen]) for (const b of this.branches.get(id) ?? []) if (b.collapsed) take(b.child);
    while (queue.length) {
      const id = queue.shift()!;
      for (const b of this.branches.get(id) ?? []) take(b.child);
      for (const b of this.incoming.get(id) ?? []) take(b.from);
    }
    return out;
  }

  /** Collapsed branches still on the canvas (their connectors carry the counters). */
  collapsedBranches(): Id[] {
    return [...this.folding.counts.keys()];
  }

  /** Recompute folded branches; returns ids whose visibility changed. */
  private refold(): Id[] {
    this.branches = branchesOf(this.doc);
    this.branchByConn.clear();
    this.incoming.clear();
    for (const list of this.branches.values()) {
      for (const b of list) {
        this.branchByConn.set(b.conn, b);
        const into = this.incoming.get(b.child);
        if (into) into.push(b);
        else this.incoming.set(b.child, [b]);
      }
    }
    const prev = this.folding.hidden;
    this.folding = computeFolding(this.doc, this.branches);
    const next = this.folding.hidden;
    const changed: Id[] = [];
    for (const id of prev) if (!next.has(id)) changed.push(id);
    for (const id of next) if (!prev.has(id)) changed.push(id);
    return changed;
  }

  /** Full rebuild (initial load). */
  rebuild(): void {
    this.index.clear();
    this.cache.clear();
    this.refold();
    const items: { id: Id; rect: Rect }[] = [];
    for (const o of this.doc.allObjects()) {
      if (o.type === CoreTypes.group || this.isFolded(o.id)) continue;
      items.push({ id: o.id, rect: indexBounds(o) });
    }
    this.index.load(items);
    this.orderDirty = true;
    this.emit(null);
  }

  /** Force re-render of objects whose display changed outside the document (module render ready). */
  invalidateObjects(ids: Id[] | null): void {
    if (ids === null) {
      this.cache.clear();
    } else {
      for (const id of ids) {
        this.cache.delete(id);
        this.forced.set(id, ++this.forcedCounter);
      }
    }
    this.emit(ids);
  }

  private onDocChange(e: ChangeEvent): void {
    const refolded = this.refold();
    for (const id of refolded) {
      const o = this.doc.get(id);
      if (!o || o.type === CoreTypes.group) continue;
      if (this.isFolded(id)) this.index.remove(id);
      else this.index.upsert(id, indexBounds(o));
    }
    for (const id of e.deleted) {
      this.index.remove(id);
      this.rank.delete(id);
      this.cache.delete(id);
    }
    for (const id of e.created) {
      const o = this.doc.get(id);
      if (!o) continue;
      if (o.type !== CoreTypes.group && !this.isFolded(id)) this.index.upsert(id, indexBounds(o));
      if (!this.orderDirty && (this.maxZ === null || o.z > this.maxZ)) {
        this.rank.set(id, this.nextRank++);
        this.maxZ = o.z;
      } else this.orderDirty = true;
    }
    for (const id of e.updated) {
      const o = this.doc.get(id);
      if (!o) continue;
      if (o.type !== CoreTypes.group && !this.isFolded(id)) this.index.upsert(id, indexBounds(o));
      const prevRank = this.rank.get(id);
      if (prevRank === undefined) this.orderDirty = true;
      else if (o.z > (this.maxZ ?? '')) {
        // moved to top: append without full resort
        this.rank.set(id, this.nextRank++);
        this.maxZ = o.z;
      } else this.orderDirty = true;
      this.cache.delete(id);
    }
    this.emit([...e.created, ...e.updated, ...e.deleted, ...refolded]);
  }

  private ensureOrder(): void {
    if (!this.orderDirty) return;
    const all = [...this.doc.allObjects()];
    all.sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : 1));
    this.rank.clear();
    all.forEach((o, i) => this.rank.set(o.id, i));
    this.maxZ = all.length ? all[all.length - 1].z : null;
    this.nextRank = all.length;
    this.orderDirty = false;
  }

  rankOf(id: Id): number {
    this.ensureOrder();
    return this.rank.get(id) ?? -1;
  }

  primitives(o: ObjectRecord): Primitive[] {
    const rev = this.doc.rev(o.id) * 1e6 + (this.forced.get(o.id) ?? 0);
    const c = this.cache.get(o.id);
    let prims: Primitive[];
    // an arrow's label being typed: the line already breaks around what is typed
    if (this.draftLabel?.id === o.id) prims = buildPrimitives({ ...o, props: { ...o.props, label: this.draftLabel.text } }, rev, this.displayCtx);
    else if (c && c.rev === rev) prims = c.prims;
    else {
      prims = buildPrimitives(o, rev, this.displayCtx);
      this.cache.set(o.id, { rev, prims });
    }
    // text being edited in a DOM overlay is hidden from the canvas
    if (this.hiddenText !== o.id) return prims;
    // module objects: only the platform's text goes (the module's own drawing, its labels included, stays)
    return o.type.startsWith('core:') ? prims.filter((p) => p.k !== 'text' && !isTextDecoration(p)) : prims.filter((p) => !isTextDecoration(p));
  }

  revOf(id: Id): number {
    return this.doc.rev(id) * 1e6 + (this.forced.get(id) ?? 0);
  }

  /** Visible items bottom → top. */
  visible(rect: Rect): RenderItem[] {
    this.ensureOrder();
    const ids = this.index.search(rect);
    const items: RenderItem[] = [];
    for (const id of ids) {
      const obj = this.doc.get(id);
      if (!obj) continue;
      const clip = this.clipOf(obj);
      // fully clipped away: nothing to draw
      if (clip && (clip.w <= 0 || clip.h <= 0 || !rectsIntersect(clip, rect))) continue;
      items.push({ id, obj, rev: this.revOf(id), prims: this.primitives(obj), clip });
    }
    items.sort((a, b) => (this.rank.get(a.id) ?? 0) - (this.rank.get(b.id) ?? 0));
    // labels of arrows go on top of every line, so another arrow never strikes through them
    const labels: RenderItem[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.obj.type !== CoreTypes.line && it.obj.type !== CoreTypes.connector) continue;
      const split = this.splitLabel(it);
      if (!split) continue;
      items[i] = split.line;
      labels.push(split.label);
    }
    items.push(...labels);
    if (this.showLinks) for (const it of this.linkItems(rect)) items.push(it);
    return items;
  }

  private labelSplits = new Map<Id, { rev: number; line: Primitive[]; label: Primitive[] }>();

  /** Line part and label part (plate + text) of an arrow's primitives, cached per revision. */
  private splitLabel(it: RenderItem): { line: RenderItem; label: RenderItem } | null {
    let s = this.labelSplits.get(it.id);
    if (!s || s.rev !== it.rev) {
      const at = it.prims.findIndex((p) => p.k !== 'path');
      s = at > 0 ? { rev: it.rev, line: it.prims.slice(0, at), label: it.prims.slice(at) } : { rev: it.rev, line: it.prims, label: [] };
      this.labelSplits.set(it.id, s);
    }
    if (!s.label.length) return null;
    return { line: { ...it, prims: s.line }, label: { ...it, id: it.id + '#label', prims: s.label } };
  }

  /**
   * Clip rect of an object: intersection of all enclosing frames that clip their
   * content (frames clip by default; props.clip === false turns it off).
   */
  clipOf(o: ObjectRecord): Rect | null {
    let root = o;
    for (let i = 0; i < 64 && root.parentId; i++) {
      const p = this.doc.get(root.parentId);
      if (!p) break;
      root = p;
    }
    let clip: Rect | null = null;
    let fid = root.frameId ?? null;
    for (let i = 0; fid && i < 64; i++) {
      const f = this.doc.get(fid);
      if (!f) break;
      if (f.props['clip'] !== false) {
        const r = boxBounds(f);
        if (!clip) clip = r;
        else {
          const x0 = Math.max(clip.x, r.x);
          const y0 = Math.max(clip.y, r.y);
          const x1 = Math.min(clip.x + clip.w, r.x + r.w);
          const y1 = Math.min(clip.y + clip.h, r.y + r.h);
          clip = { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
        }
      }
      fid = f.frameId ?? null;
    }
    return clip;
  }

  /** Hidden (clipped) parts of an object are not clickable. */
  private insideClip(o: ObjectRecord, p: Vec2): boolean {
    const c = this.clipOf(o);
    return !c || (p.x >= c.x && p.x <= c.x + c.w && p.y >= c.y && p.y <= c.y + c.h);
  }

  /** Draw typed data links as routed dashed arrows (not selectable objects). */
  showLinks = true;
  private linkVis = new Map<Id, { key: string; item: RenderItem; rect: Rect }>();
  private linkRev = 0;

  private linkItems(rect: Rect): RenderItem[] {
    const out: RenderItem[] = [];
    const seen = new Set<Id>();
    for (const l of this.doc.allLinks()) {
      const a = this.doc.get(l.from);
      const b = this.doc.get(l.to);
      if (!a || !b) continue;
      seen.add(l.id);
      const key = this.doc.rev(l.from) + ':' + this.doc.rev(l.to) + ':' + l.directed + ':' + l.type;
      let c = this.linkVis.get(l.id);
      if (!c || c.key !== key) {
        c = this.buildLink(l, key);
        this.linkVis.set(l.id, c);
      }
      if (rectsIntersect(c.rect, rect)) out.push(c.item);
    }
    for (const id of [...this.linkVis.keys()]) if (!seen.has(id)) this.linkVis.delete(id);
    return out;
  }

  private buildLink(l: LinkRecord, key: string): { key: string; item: RenderItem; rect: Rect } {
    const r = computeRoute(this.doc, { objectId: l.from, point: { x: 0, y: 0 } }, { objectId: l.to, point: { x: 0, y: 0 } }, 'orthogonal', (q) => this.index.search(q));
    const box = pointsBox(r.pts);
    const obj: ObjectRecord = {
      id: 'link:' + l.id,
      canvasId: this.doc.canvasId,
      type: 'core:link',
      formatVersion: 1,
      parentId: null,
      z: '~',
      x: box.x,
      y: box.y,
      w: box.w,
      h: box.h,
      rotation: 0,
      locked: true,
      style: { stroke: { color: '#8b5cf6', width: 2, dash: 'dashed' }, fill: { kind: 'none' }, opacity: 0.9 },
      props: {},
    };
    const prims: Primitive[] = [
      { k: 'path', points: box.local, closed: false, fill: { kind: 'none' }, stroke: { color: '#8b5cf6', width: 2, dash: 'dashed' }, arrowEnd: l.directed },
    ];
    const pad = 12;
    return { key, item: { id: obj.id, obj, rev: ++this.linkRev, prims }, rect: { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 } };
  }

  /** Objects intersecting / contained in a world rect (leaf objects, unsorted). */
  query(rect: Rect): Id[] {
    return this.index.search(rect);
  }

  /** Topmost leaf object at a world point; `tol` in world units (§4.5). */
  hitTest(p: Vec2, tol: number, filter?: (o: ObjectRecord) => boolean): Id | null {
    this.ensureOrder();
    const ids = this.index.search({ x: p.x - tol, y: p.y - tol, w: tol * 2, h: tol * 2 });
    let best: Id | null = null;
    let bestRank = -Infinity;
    for (const id of ids) {
      const o = this.doc.get(id);
      if (!o || (filter && !filter(o))) continue;
      const r = this.rank.get(id) ?? 0;
      if (r <= bestRank) continue;
      if (this.hitObject(o, p, tol) && this.insideClip(o, p)) {
        best = id;
        bestRank = r;
      }
    }
    return best;
  }

  /** API 2.2: the clickable area (primitive `action`) of an object under a world point, topmost first. */
  actionAt(o: ObjectRecord, p: Vec2): { id: string; data?: string | number | boolean | null } | null {
    const l = worldToLocal(o, p);
    const prims = this.primitives(o);
    for (let i = prims.length - 1; i >= 0; i--) {
      const pr = prims[i];
      if (!('action' in pr) || !pr.action) continue;
      let x0: number, y0: number, x1: number, y1: number;
      if (pr.k === 'path') {
        x0 = Infinity; y0 = Infinity; x1 = -Infinity; y1 = -Infinity;
        for (let j = 0; j < pr.points.length; j += 2) {
          x0 = Math.min(x0, pr.points[j]); x1 = Math.max(x1, pr.points[j]);
          y0 = Math.min(y0, pr.points[j + 1]); y1 = Math.max(y1, pr.points[j + 1]);
        }
      } else {
        x0 = pr.x; y0 = pr.y; x1 = pr.x + pr.w; y1 = pr.y + pr.h;
      }
      if (l.x >= x0 && l.x <= x1 && l.y >= y0 && l.y <= y1) return pr.action;
    }
    return null;
  }

  /** All leaf objects under a point, topmost first. */
  hitTestAll(p: Vec2, tol: number): Id[] {
    this.ensureOrder();
    const ids = this.index.search({ x: p.x - tol, y: p.y - tol, w: tol * 2, h: tol * 2 });
    return ids
      .filter((id) => {
        const o = this.doc.get(id);
        return o ? this.hitObject(o, p, tol) && this.insideClip(o, p) : false;
      })
      .sort((a, b) => (this.rank.get(b) ?? 0) - (this.rank.get(a) ?? 0));
  }

  private hitObject(o: ObjectRecord, p: Vec2, tol: number): boolean {
    const l = worldToLocal(o, p);
    const prims = this.primitives(o);
    const top = o.type === CoreTypes.frame ? frameTitleHeight(o) : 0;
    const inBox = l.x >= -tol && l.y >= -tol - top && l.x <= o.w + tol && l.y <= o.h + tol;
    if (o.type === CoreTypes.line || o.type === CoreTypes.connector || o.type === CoreTypes.pen || o.type === CoreTypes.path) {
      for (const pr of prims) {
        // an arrow's label is part of it
        if (pr.k === 'text' && l.x >= pr.x - tol && l.x <= pr.x + pr.w + tol && l.y >= pr.y - tol && l.y <= pr.y + pr.h + tol) return true;
        if (pr.k !== 'path') continue;
        // a filled closed path is solid inside
        const holes = pr.closed ? pr.holes ?? [] : [];
        if (pr.closed && pr.fill.kind !== 'none' && pointInPolygon(l.x, l.y, pr.points) && !holes.some((h) => pointInPolygon(l.x, l.y, h))) return true;
        const sw = pr.widths?.length ? Math.max(pr.stroke?.width ?? 1, ...pr.widths) : (pr.stroke?.width ?? 1);
        if (distToPolyline(l.x, l.y, pr.points, pr.closed) <= tol + sw / 2) return true;
        for (const h of holes) if (distToPolyline(l.x, l.y, h, true) <= tol + sw / 2) return true;
      }
      return false;
    }
    if (!inBox) return false;
    if (o.type === CoreTypes.ellipse || o.type === CoreTypes.triangle || o.type === CoreTypes.diamond) {
      const shape = o.type === CoreTypes.ellipse ? 'ellipse' : o.type === CoreTypes.triangle ? 'triangle' : 'diamond';
      const poly = shapeOutline(shape, 0, 0, o.w, o.h, 0, 32);
      return pointInPolygon(l.x, l.y, poly) || distToPolyline(l.x, l.y, poly, true) <= tol + (o.style.stroke?.width ?? 0) / 2;
    }
    return true;
  }
}

/** Bounds used by the spatial index: rotated box plus stroke/arrow padding. */
export function indexBounds(o: ObjectRecord): Rect {
  const b = boxBounds(o);
  let pad = (o.style.stroke?.width ?? 0) / 2;
  if (o.type === CoreTypes.line || o.type === CoreTypes.connector) {
    pad += arrowSize(o.style.stroke?.width ?? 1);
    // a long label may stick out past the ends
    const label = o.props['label'];
    if (typeof label === 'string' && label) pad += (o.style.text?.fontSize ?? 18) * (2.2 + label.length * 0.35);
  }
  const top = o.type === CoreTypes.frame ? frameTitleHeight(o) : 0;
  return { x: b.x - pad, y: b.y - pad - top, w: b.w + pad * 2, h: b.h + pad * 2 + top };
}
